import { useCallback, useEffect, useState } from 'react';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { bankReconApi, type BankAccount, type BankAccountInput, type BankAccountType } from '@/api/bankReconApi';
import { formatINR } from '@/utils/format';

const fieldCls =
  'w-full h-10 px-3 text-sm bg-white border border-[#e2e8f0] rounded-lg focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-indigo-100 text-[#1e293b]';
const labelCls = 'block text-xs font-semibold text-[#64748b] mb-1.5 uppercase tracking-wide';

const ACCOUNT_TYPES: { value: BankAccountType; label: string }[] = [
  { value: 'CURRENT', label: 'Current' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'OVERDRAFT', label: 'Overdraft (OD)' },
  { value: 'CASH_CREDIT', label: 'Cash Credit (CC)' },
];

const emptyForm: BankAccountInput = {
  bankName: '',
  accountNumber: '',
  accountName: '',
  ifscCode: '',
  branchName: '',
  accountType: 'CURRENT',
  accountHolderName: '',
  openingBalance: 0,
  openingBalanceDate: '',
  isPrimary: false,
};

const maskAccount = (n: string) => (n.length > 4 ? `${'•'.repeat(Math.min(n.length - 4, 8))}${n.slice(-4)}` : n);

/**
 * Company bank accounts (Settings → Company). Each account gets its own ledger in the
 * Chart of Accounts, is available for receipts / payments and Bank Reconciliation, and
 * the primary account is printed on invoices.
 */
export default function BankAccountsSection() {
  const toast = useToast();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<BankAccountInput>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDeactivate, setConfirmDeactivate] = useState<BankAccount | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setAccounts(await bankReconApi.getBankAccounts({ includeInactive: true }));
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, isPrimary: accounts.filter((a) => a.isActive).length === 0 });
    setFormOpen(true);
  };

  const openEdit = (acc: BankAccount) => {
    setEditing(acc);
    setForm({
      bankName: acc.bankName,
      accountNumber: acc.accountNumber,
      accountName: acc.accountName,
      ifscCode: acc.ifscCode || '',
      branchName: acc.branchName || '',
      accountType: acc.accountType,
      accountHolderName: acc.accountHolderName || '',
      openingBalance: acc.openingBalance,
      openingBalanceDate: acc.openingBalanceDate || '',
      isPrimary: acc.isPrimary,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.bankName.trim() || !form.accountNumber.trim()) {
      toast.error('Bank name and account number are required');
      return;
    }
    setSaving(true);
    const payload: BankAccountInput = {
      ...form,
      bankName: form.bankName.trim(),
      accountNumber: form.accountNumber.replace(/\s/g, ''),
      ifscCode: form.ifscCode?.trim().toUpperCase() || undefined,
      accountName: form.accountName?.trim() || undefined,
      openingBalance: Number(form.openingBalance) || 0,
      openingBalanceDate: form.openingBalanceDate || null,
    };
    try {
      if (editing) {
        await bankReconApi.updateBankAccount(editing.id, payload);
        toast.success('Bank account updated');
      } else {
        await bankReconApi.createBankAccount(payload);
        toast.success('Bank account added — a ledger was created in the Chart of Accounts');
      }
      setFormOpen(false);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to save bank account');
    } finally {
      setSaving(false);
    }
  };

  const setPrimary = async (acc: BankAccount) => {
    try {
      await bankReconApi.updateBankAccount(acc.id, { isPrimary: true });
      toast.success(`${acc.accountName} is now the primary account (printed on invoices)`);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update bank account');
    }
  };

  const setActive = async (acc: BankAccount, isActive: boolean) => {
    try {
      await bankReconApi.updateBankAccount(acc.id, { isActive });
      toast.success(isActive ? 'Bank account reactivated' : 'Bank account deactivated');
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update bank account');
    }
  };

  const visible = accounts.filter((a) => showInactive || a.isActive);
  const inactiveCount = accounts.filter((a) => !a.isActive).length;

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-xl p-6">
      <div className="flex items-start justify-between gap-3 mb-4 pb-3 border-b border-[#e2e8f0]">
        <div>
          <h3 className="text-sm font-semibold text-[#1e293b]">Bank Accounts</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Used for receipts, payments and Bank Reconciliation. The primary account is printed on invoices.
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-medium hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line" /> Add Bank Account
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center text-slate-400 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2" /> Loading bank accounts…
        </div>
      ) : visible.length === 0 ? (
        <div className="py-8 text-center">
          <i className="ri-bank-line text-3xl text-slate-300" />
          <p className="text-sm font-semibold text-slate-700 mt-1">No bank accounts yet</p>
          <p className="text-xs text-slate-400">Add your company's bank account to print it on invoices and reconcile statements.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {visible.map((acc) => (
            <div
              key={acc.id}
              className={`border rounded-xl p-4 flex items-start justify-between gap-4 flex-wrap ${
                acc.isPrimary ? 'border-indigo-200 bg-indigo-50/30' : 'border-[#e2e8f0]'
              } ${acc.isActive ? '' : 'opacity-60'}`}
            >
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 text-[#4f46e5] flex items-center justify-center shrink-0">
                  <i className="ri-bank-line text-lg" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#1e293b]">{acc.bankName}</p>
                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-[#e2e8f0]">
                      {ACCOUNT_TYPES.find((t) => t.value === acc.accountType)?.label || acc.accountType}
                    </span>
                    {acc.isPrimary && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Primary</span>
                    )}
                    {!acc.isActive && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">Inactive</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-600 mt-0.5 font-mono">
                    A/c {maskAccount(acc.accountNumber)} · IFSC {acc.ifscCode || '—'}
                    {acc.branchName ? ` · ${acc.branchName}` : ''}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Ledger {acc.ledgerCode} — {acc.ledgerName} · Book balance {formatINR(acc.currentBookBalance)}
                    {acc.lastReconciledDate ? ` · Reconciled to ${acc.lastReconciledDate}` : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {acc.isActive && !acc.isPrimary && (
                  <button
                    type="button"
                    onClick={() => setPrimary(acc)}
                    className="h-8 px-3 rounded-lg border border-[#e2e8f0] bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 cursor-pointer"
                  >
                    Make Primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => openEdit(acc)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 cursor-pointer"
                  title="Edit"
                >
                  <i className="ri-pencil-line" />
                </button>
                {acc.isActive ? (
                  <button
                    type="button"
                    onClick={() => setConfirmDeactivate(acc)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 cursor-pointer"
                    title="Deactivate"
                  >
                    <i className="ri-forbid-line" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setActive(acc, true)}
                    className="h-8 px-3 rounded-lg border border-[#e2e8f0] bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 cursor-pointer"
                  >
                    Reactivate
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {inactiveCount > 0 && (
        <button
          type="button"
          onClick={() => setShowInactive((v) => !v)}
          className="mt-3 text-xs text-slate-500 hover:text-[#4f46e5] cursor-pointer"
        >
          {showInactive ? 'Hide' : 'Show'} {inactiveCount} inactive account{inactiveCount > 1 ? 's' : ''}
        </button>
      )}

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col border border-[#e2e8f0]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
              <div>
                <h2 className="text-base font-bold text-[#1e293b]">{editing ? 'Edit Bank Account' : 'Add Bank Account'}</h2>
                <p className="text-xs text-slate-500">
                  {editing ? `Ledger ${editing.ledgerCode} — ${editing.ledgerName}` : 'A matching ledger is created in Chart of Accounts → Cash & Bank'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-500 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className={labelCls}>Bank Name *</label>
                  <input
                    type="text"
                    value={form.bankName}
                    onChange={(e) => setForm((p) => ({ ...p, bankName: e.target.value }))}
                    placeholder="e.g. State Bank of India"
                    className={fieldCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Account Number *</label>
                  <input
                    type="text"
                    value={form.accountNumber}
                    onChange={(e) => setForm((p) => ({ ...p, accountNumber: e.target.value }))}
                    placeholder="Account number"
                    className={`${fieldCls} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelCls}>IFSC Code</label>
                  <input
                    type="text"
                    value={form.ifscCode}
                    maxLength={11}
                    onChange={(e) => setForm((p) => ({ ...p, ifscCode: e.target.value.toUpperCase() }))}
                    placeholder="11 characters"
                    className={`${fieldCls} font-mono`}
                  />
                </div>
                <div>
                  <label className={labelCls}>Branch</label>
                  <input
                    type="text"
                    value={form.branchName}
                    onChange={(e) => setForm((p) => ({ ...p, branchName: e.target.value }))}
                    className={fieldCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Account Type</label>
                  <select
                    value={form.accountType}
                    onChange={(e) => setForm((p) => ({ ...p, accountType: e.target.value as BankAccountType }))}
                    className={fieldCls}
                  >
                    {ACCOUNT_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>
                        {t.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Account Holder Name</label>
                  <input
                    type="text"
                    value={form.accountHolderName}
                    onChange={(e) => setForm((p) => ({ ...p, accountHolderName: e.target.value }))}
                    className={fieldCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Display Name</label>
                  <input
                    type="text"
                    value={form.accountName}
                    onChange={(e) => setForm((p) => ({ ...p, accountName: e.target.value }))}
                    placeholder="Defaults to Bank - last 4 digits"
                    className={fieldCls}
                  />
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-700">Bank statement starting point (for reconciliation)</p>
                <p className="text-[11px] text-slate-400 mb-3">
                  The balance on the bank statement on the day you start reconciling. Book entries before this date are treated as already cleared.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Statement Balance (₹)</label>
                    <input
                      type="number"
                      value={form.openingBalance}
                      onChange={(e) => setForm((p) => ({ ...p, openingBalance: parseFloat(e.target.value) || 0 }))}
                      className={fieldCls}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>As On Date</label>
                    <input
                      type="date"
                      value={form.openingBalanceDate || ''}
                      onChange={(e) => setForm((p) => ({ ...p, openingBalanceDate: e.target.value }))}
                      className={fieldCls}
                    />
                  </div>
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={!!form.isPrimary}
                  disabled={editing?.isPrimary}
                  onChange={(e) => setForm((p) => ({ ...p, isPrimary: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                Primary account — print on invoices and use as default bank
              </label>
            </div>

            <div className="px-6 py-4 border-t border-[#e2e8f0] bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setFormOpen(false)}
                className="h-9 px-4 text-sm font-medium text-slate-700 border border-[#e2e8f0] bg-white hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="h-9 px-4 text-sm font-medium text-white bg-[#4f46e5] hover:bg-indigo-700 rounded-lg cursor-pointer disabled:opacity-60"
              >
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Bank Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeactivate}
        title="Deactivate Bank Account"
        message={`Deactivate ${confirmDeactivate?.accountName}? It will no longer be offered for payments or reconciliation. Its history is kept.`}
        variant="warning"
        onConfirm={() => {
          const acc = confirmDeactivate;
          setConfirmDeactivate(null);
          if (acc) setActive(acc, false);
        }}
        onCancel={() => setConfirmDeactivate(null)}
      />
    </div>
  );
}
